/**
 * The emoji set, and the custom emoji staff upload.
 *
 * Codepoint ranges are expanded once and filtered by whether the platform can
 * actually draw each glyph - the ranges include reserved slots that would
 * otherwise show as tofu boxes.
 */

import { api } from './api';
import { CATEGORIES, COUNTRIES, FREQUENT, KEYWORDS, flagOf } from './emoji-data';

export type CustomEmoji = {
  id: string;
  name: string;
  url: string;
  animated?: boolean;
  /** Who the staff console has reserved it for. */
  access?: 'everyone' | 'premium' | 'vip' | 'staff';
  /** False when this account is not on a plan that may use it. */
  usable?: boolean;
};
export type EmojiCategory = { label: string; list: string[]; icon: string };

let customList: CustomEmoji[] = [];
const customByName = new Map<string, CustomEmoji>();

export function customEmoji(): CustomEmoji[] { return customList; }
export function customEmojiByName(name: string): CustomEmoji | null {
  return customByName.get(name.toLowerCase()) ?? null;
}

export function setCustomEmoji(list: CustomEmoji[] | undefined) {
  customList = Array.isArray(list) ? list : [];
  customByName.clear();
  for (const e of customList) customByName.set(e.name.toLowerCase(), e);
}

export async function loadCustomEmoji(): Promise<CustomEmoji[]> {
  try {
    const data = await api.get<{ emoji: CustomEmoji[] }>('/api/emoji');
    setCustomEmoji(data.emoji);
  } catch {
    /* the picker just shows the built-in set */
  }
  return customList;
}

/** Country names, keyed by their flag, for search. */
const FLAG_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRIES).map(([code, name]) => [flagOf(code), (name + ' ' + code + ' flag').toLowerCase()])
);

export function emojiKeywords(ch: string): string {
  // Flags have no hand-written keywords - their country name is the keyword.
  return KEYWORDS[ch] ?? FLAG_NAMES[ch] ?? '';
}

export { FREQUENT };

let cached: EmojiCategory[] | null = null;

/** Builds the browsable grid once, dropping anything the platform cannot draw. */
export function emojiSet(): EmojiCategory[] {
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 18;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  // Without a 2D context there is no way to test glyph coverage; showing the
  // whole range with some tofu beats showing nothing.
  const renders = ctx
    ? (ch: string) => {
        ctx.clearRect(0, 0, 18, 18);
        ctx.fillText(ch, 0, 0);
        const { data } = ctx.getImageData(0, 0, 18, 18);
        for (let i = 3; i < data.length; i += 4) if (data[i] !== 0) return true;
        return false;
      }
    : () => true;

  if (ctx) {
    ctx.font = '16px sans-serif';
    ctx.textBaseline = 'top';
  }

  cached = CATEGORIES.map(([label, ranges, icon]) => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const [from, to] of ranges) {
      for (let cp = from; cp <= to; cp++) {
        const ch = String.fromCodePoint(cp);
        if (seen.has(ch)) continue;
        seen.add(ch);
        if (renders(ch)) list.push(ch);
      }
    }
    return { label, list, icon };
  });

  /*
   * Flags, built rather than swept.
   *
   * Each is a pair of regional indicators, so no codepoint range finds them -
   * which is why there were none at all. Still filtered through renders(), so
   * a system whose font draws letter pairs instead of flags shows none rather
   * than 250 pairs of letters.
   */
  const flags = Object.keys(COUNTRIES).map(flagOf).filter(renders);
  if (flags.length) cached.push({ label: 'Flags', list: flags, icon: 'globe' });

  return cached;
}

/** Everything matching a search, across every category plus the custom set. */
export function searchEmoji(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: string[] = [];
  for (const cat of emojiSet()) {
    for (const ch of cat.list) {
      if (emojiKeywords(ch).includes(q)) out.push(ch);
    }
  }
  return out;
}
